/** Request(의뢰) 관련 API 호출 wrapper (M25). */

import type { AxiosProgressEvent } from "axios";

import { apiClient } from "./client";
import { appendAutoReview, type CsvType, type JobOut } from "./jobs";

export interface RequestOut {
  id: string;
  title: string | null;
  source_name: string | null;
  created_at: string;
  status: string;
  job_count: number;
  file_count: number;
  success_count: number;
  rejected_count: number;
  error_count: number;
  reviewed_count: number;
  done_jobs: number;
}

export interface RequestDetailOut extends RequestOut {
  jobs: JobOut[];
}

export interface CreateRequestOptions {
  /** ZIP 파일(폴더 단위 작업 분할). files 와 배타. */
  zip?: File;
  /** PDF 직접 업로드(단일 작업). zip 과 배타. */
  files?: File[];
  title?: string;
  /** M26 — 추출 후 자동 검수를 진행할지 (모든 작업에 적용). */
  autoReview?: boolean;
  /** 자동 검수 결과 문장 언어 (화면 언어). */
  locale?: string;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

export async function createRequest({
  zip,
  files,
  title,
  autoReview,
  locale,
  onUploadProgress,
  signal,
}: CreateRequestOptions): Promise<RequestDetailOut> {
  const form = new FormData();
  if (zip) {
    form.append("zip", zip, zip.name);
  }
  for (const f of files ?? []) {
    form.append("files", f, f.name);
  }
  if (title?.trim()) {
    form.append("title", title.trim());
  }
  appendAutoReview(form, autoReview, locale);
  const res = await apiClient.post<RequestDetailOut>("/requests", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    signal,
    timeout: 30 * 60 * 1000,
  });
  return res.data;
}

export async function listRequests(): Promise<RequestOut[]> {
  const res = await apiClient.get<RequestOut[]>("/requests");
  return res.data;
}

export async function getRequest(requestId: string): Promise<RequestDetailOut> {
  const res = await apiClient.get<RequestDetailOut>(`/requests/${requestId}`);
  return res.data;
}

export async function updateRequest(requestId: string, title: string | null): Promise<RequestOut> {
  const res = await apiClient.patch<RequestOut>(`/requests/${requestId}`, { title });
  return res.data;
}

export async function deleteRequest(requestId: string): Promise<void> {
  await apiClient.delete(`/requests/${requestId}`);
}

/** 의뢰 CSV 미리보기의 작업(폴더) 단위 그룹. */
export interface RequestCsvGroup {
  job_id: string;
  folder: string;
  count: number;
  rows: string[][];
}

/** 의뢰 CSV 미리보기 — 작업(폴더)별 그룹으로 분리. */
export interface RequestCsvPreview {
  headers: string[];
  groups: RequestCsvGroup[];
  total: number;
}

/** content-disposition 헤더에서 RFC 5987 filename* 우선 파싱. */
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fallthrough */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? fallback;
}

/** 의뢰 CSV 다운로드 — 작업별 폴더 구조 ZIP. (blob, 서버가 정한 파일명) 반환. */
export async function downloadRequestCsv(
  requestId: string,
  type: CsvType,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get<Blob>(`/requests/${requestId}/csv`, {
    params: { type },
    responseType: "blob",
  });
  const disposition = res.headers?.["content-disposition"] as string | undefined;
  return { blob: res.data, filename: filenameFromDisposition(disposition, `${type}.zip`) };
}

/** 의뢰 원본 PDF 일괄 다운로드 — 작업별 폴더 구조 ZIP. (blob, 서버 파일명) 반환. */
export async function downloadRequestPdfZip(
  requestId: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get<Blob>(`/requests/${requestId}/pdf-zip`, {
    responseType: "blob",
  });
  const disposition = res.headers?.["content-disposition"] as string | undefined;
  return { blob: res.data, filename: filenameFromDisposition(disposition, "request_pdfs.zip") };
}

export async function getRequestCsvPreview(
  requestId: string,
  type: CsvType,
): Promise<RequestCsvPreview> {
  const res = await apiClient.get<RequestCsvPreview>(`/requests/${requestId}/csv/preview`, {
    params: { type },
  });
  return res.data;
}
